import { Image } from "db/definitions";
import { recordSentry } from "utils/recordSentry";
import { createImageBinaryAndSave } from "../db/queries/imageBinaryQueries";
import { createImageForBatchResolver, saveImageDoc } from "../db/queries/imageQueries";
import _removeImage from "./_removeImage";
import { renameFile } from "./renameFile";
import { handleToolboxResponse } from "./s3/handleToolboxResponse";
import createImageRoute from "./toolboxQueries/createImageRoute";
import { updateImageDocInDb } from "./updateImageDocInDb";

/**
 * Handles parsing, compressing, uploading and removing existing images.
 * @param oldValues the initial image values
 * @param newValues the updated image values
 * @param group to put the imagea in
 * @param direction The direction we need to upload the image (and by extension, delete old image)
 * @param online Whether or not the user is online
 * @param dontRename
 * @param skipUpdateImageDoc determines if the image doc itself will be updated in the call.
 * @returns updated image object.
 */
async function updateImageDoc(
	oldValues: Pick<Image, "key"> & Partial<Image>,
	newValues: Image,
	group: string,
	direction: "UP" | "DOWN",
	online: boolean,
	dontRename?: boolean,
	skipUpdateImageDoc?: boolean,
	fileNameOverride?: string,
): Promise<Image> {
	try {
		const updatedImage = createImageForBatchResolver(newValues);
		let keyName = newValues.key;
		if (typeof keyName === "object" && keyName !== null && "files" in keyName && keyName.files && typeof keyName.files === "string") {
			keyName = keyName.files;
			updatedImage.key = keyName;
		}
		if (oldValues?.key !== keyName) {
			if (typeof newValues.key === "object" && newValues.key !== null && "files" in newValues.key && newValues.key.files.length > 0) {
				const fileToUpload = newValues.key.files[0];
				// parse the name
				const newFile = dontRename ? fileToUpload : renameFile(fileToUpload, group, oldValues?.key || "", fileNameOverride);
				const newKey = await saveImageToS3OrIndexeddb(newFile, online, direction);
				updatedImage.key = newKey;
			} else {
				// new key is cleared, so just need to update the obj - Just set the key to "", because the newValues could be key.files === "" and it needs to be just a string
				updatedImage.key = "";
			}

			// If an image doc is updated without changing the image, we don't want to delete the old image
			if (oldValues.key && oldValues.key !== updatedImage.key && typeof oldValues.key === "string") {
				// the old value is a key to an image on s3 that needs to be removed.
				_removeImage(oldValues.key, online, direction);
			}
		}

		if (!skipUpdateImageDoc) {
			try {
				updatedImage.updatedAt = new Date().toISOString();
				if (direction === "UP" && online) {
					await updateImageDocInDb(updatedImage);
				} else if (direction === "DOWN") {
					await saveImageDoc(updatedImage);
				}
			} catch (error) {
				recordSentry({ error, msg: "Error updating image:" });
				throw error;
			}
		}

		// return the updated image doc with the key as a string if it was uploaded successfully.
		return updatedImage;
	} catch (error) {
		recordSentry({ error, msg: "Error in updateImageDoc" });
		throw error;
	}
}

/**
 * Saves the image to s3 or the indexed db, returning the key
 * @author Eric Webb <ewebb@factorearth.com>
 * @param file The image file we want to save
 * @param online Whether or not the user is online
 * @param direction The direction they want to save
 */
async function saveImageToS3OrIndexeddb(file: File, online: boolean, direction: "UP" | "DOWN"): Promise<string> {
	if (online && direction === "UP") {
		try {
			const response = await createImageRoute(file);
			if (response) {
				const newKey = handleToolboxResponse(response);
				return newKey;
			}
		} catch (err) {
			recordSentry({ error: err, msg: "Error compressing image:" });
		}
	} else if (direction === "DOWN") {
		const imageBinary = await createImageBinaryAndSave(file.name, file);
		return imageBinary.key;
	}
	return "";
}

export default updateImageDoc;
